<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <DashboardNav />

    <main class="content">
      <!-- Global stat row -->
      <div class="stat-row">
        <StatCard label="PROJECTS" :value="stats.total_projects.toString()" sub="active" />
        <StatCard label="MESSAGES" :value="stats.total_messages.toLocaleString()" sub="all time" />
        <StatCard
          label="AVG ACCURACY"
          :value="stats.avg_accuracy > 0 ? stats.avg_accuracy.toFixed(1) + '%' : '—'"
          sub="latest eval scores"
          :trend="accuracyTrend"
        />
        <StatCard label="TOTAL SPEND" :value="`$${stats.total_cost.toFixed(4)}`" sub="all time" />
        <StatCard
          label="ACTIVE JOBS"
          :value="stats.active_jobs.toString()"
          sub="queued + processing"
          :trend="stats.active_jobs > 0 ? 'neutral' : 'positive'"
        />
      </div>

      <!-- Main grid -->
      <div class="main-grid">
        <!-- Projects table -->
        <div class="projects-col">
          <div class="col-header">
            <SectionHeader label="Projects" :meta="`${projects.length} total`" />
            <div class="header-btns">
              <button class="new-btn ghost-btn" @click="showWizard = true">WIZARD</button>
              <button class="new-btn" @click="showModal = true">+ NEW PROJECT</button>
            </div>
          </div>

          <div v-if="projects.length === 0" class="empty-state">
            <div class="empty-icon">S</div>
            <p class="empty-title">No projects yet</p>
            <p class="empty-body">Use the setup wizard to create your first project — it walks you through adding an AI client, creating a project, and assigning clients in one flow.</p>
            <div class="empty-cta-row">
              <button class="empty-cta" @click="showWizard = true">GET STARTED →</button>
              <button class="empty-cta-ghost" @click="showModal = true">Quick create</button>
            </div>
          </div>

          <div v-else class="project-table">
            <div class="table-header">
              <span class="col-name">PROJECT</span>
              <span class="col-adapter">ADAPTER</span>
              <span class="col-uuid">UUID</span>
            </div>
            <div
              v-for="project in projects"
              :key="project.uuid"
              class="table-row"
              @click="visit(project.uuid)"
            >
              <span class="col-name project-name">{{ project.name }}</span>
              <span class="col-adapter">
                <span class="adapter-badge">{{ project.rag_adapter }}</span>
              </span>
              <span class="col-uuid">{{ project.uuid }}</span>
              <span class="row-arrow">→</span>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="right-col">
          <!-- Live feed -->
          <div class="feed-panel">
            <div class="panel-head">
              <span class="panel-label">LIVE FEED</span>
              <span class="panel-meta">recent events</span>
            </div>
            <div class="feed-body">
              <div v-if="live_feed.length === 0" class="feed-empty">No events yet</div>
              <LiveFeedRow
                v-for="(event, i) in live_feed"
                :key="i"
                :type="event.type"
                :message="event.message"
                :created-at="event.created_at"
                :index="i"
              />
            </div>
          </div>

          <!-- Provider split -->
          <div v-if="providerEntries.length > 0" class="provider-panel">
            <div class="panel-head">
              <span class="panel-label">PROVIDER SPLIT</span>
              <span class="panel-meta">by messages</span>
            </div>
            <div class="provider-body">
              <div v-for="[provider, count] in providerEntries" :key="provider" class="provider-row">
                <span class="provider-name">{{ provider }}</span>
                <div class="provider-bar-wrap">
                  <div
                    class="provider-bar"
                    :style="{
                      width: (count / totalProviderMessages * 100).toFixed(1) + '%',
                      background: providerColor(provider),
                    }"
                  />
                </div>
                <span class="provider-count mono">{{ count.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Setup Wizard -->
    <SetupWizard v-if="showWizard" @close="showWizard = false" />

    <!-- New Project Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal-header">
            <span class="modal-title">NEW PROJECT</span>
            <button class="modal-close" @click="closeModal">✕</button>
          </div>

          <form class="modal-form" @submit.prevent="createProject">
            <div class="field">
              <label class="field-label">NAME</label>
              <input
                v-model="form.name"
                class="field-input"
                type="text"
                placeholder="My AI Project"
                required
                :disabled="creating"
              />
            </div>

            <div class="field">
              <label class="field-label">DESCRIPTION</label>
              <textarea
                v-model="form.description"
                class="field-input field-textarea"
                placeholder="What does this project do? The AI will use this to design a pipeline."
                rows="3"
                required
                :disabled="creating"
              />
            </div>

            <div class="field">
              <label class="field-label">RAG ADAPTER</label>
              <select v-model="form.rag_adapter" class="field-input field-select" :disabled="creating">
                <option value="pgvector">pgvector (default)</option>
                <option value="qdrant">qdrant</option>
              </select>
            </div>

            <div v-if="createError" class="form-error">{{ createError }}</div>

            <div class="modal-actions">
              <button type="button" class="cancel-btn" :disabled="creating" @click="closeModal">
                CANCEL
              </button>
              <button type="submit" class="submit-btn" :disabled="creating">
                {{ creating ? "CREATING…" : "CREATE PROJECT" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import DashboardNav from "../../components/DashboardNav.vue"
import StatCard from "../../components/StatCard.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import LiveFeedRow from "../../components/LiveFeedRow.vue"
import SetupWizard from "../../components/SetupWizard.vue"

const props = defineProps({
  projects:       { type: Array,  default: () => [] },
  stats:          { type: Object, default: () => ({ total_projects: 0, total_messages: 0, avg_accuracy: 0, total_cost: 0, active_jobs: 0 }) },
  provider_split: { type: Object, default: () => ({}) },
  live_feed:      { type: Array,  default: () => [] },
})

const showModal   = ref(false)
const showWizard  = ref(false)
const creating    = ref(false)
const createError = ref(null)
const form = ref({ name: "", description: "", rag_adapter: "pgvector" })

const accuracyTrend = computed(() => {
  const s = props.stats.avg_accuracy
  if (s >= 90) return "positive"
  if (s >= 70) return "neutral"
  if (s > 0)   return "negative"
  return "neutral"
})

const providerEntries = computed(() =>
  Object.entries(props.provider_split).sort((a, b) => b[1] - a[1])
)

const totalProviderMessages = computed(() =>
  providerEntries.value.reduce((sum, [, count]) => sum + count, 0) || 1
)

function providerColor(p) {
  const map = { openai: "#4ade80", anthropic: "#60a5fa", google: "#c084fc" }
  return map[p?.toLowerCase()] ?? "#f5a623"
}

function visit(uuid) {
  router.visit(`/projects/${uuid}`)
}

function closeModal() {
  if (creating.value) return
  showModal.value   = false
  createError.value = null
  form.value = { name: "", description: "", rag_adapter: "pgvector" }
}

async function createProject() {
  creating.value    = true
  createError.value = null

  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch("/api/v1/projects", {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body:    JSON.stringify(form.value),
    })
    const data = await res.json()

    if (res.ok) {
      showModal.value = false
      router.visit(`/projects/${data.project.uuid}/settings`)
    } else {
      createError.value = data.message || "Failed to create project"
    }
  } catch {
    createError.value = "Network error — please try again"
  } finally {
    creating.value = false
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 16px;
  align-items: start;
}

.projects-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.new-btn {
  padding: 7px 14px;
  background: rgba(245, 166, 35, 0.12);
  border: 1px solid rgba(245, 166, 35, 0.25);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #f5a623;
  cursor: pointer;
  transition: all 0.2s;
}

.new-btn:hover {
  background: rgba(245, 166, 35, 0.2);
  border-color: rgba(245, 166, 35, 0.4);
}

.empty-state {
  padding: 48px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

.empty-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f5a623, #e8920a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 18px;
  color: #0a0a0b;
  margin: 0 auto 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 6px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0 0 20px;
}

.empty-cta {
  padding: 9px 20px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.empty-cta:hover { opacity: 0.85; }

.empty-cta-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
}

.empty-cta-ghost {
  padding: 9px 18px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(232, 230, 227, 0.4);
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}
.empty-cta-ghost:hover { color: #e8e6e3; border-color: rgba(255, 255, 255, 0.2); }

.header-btns { display: flex; gap: 8px; align-items: center; }

.ghost-btn {
  background: transparent !important;
  border: 1px solid rgba(245, 166, 35, 0.2) !important;
  color: rgba(245, 166, 35, 0.5) !important;
}
.ghost-btn:hover {
  background: rgba(245, 166, 35, 0.06) !important;
  border-color: rgba(245, 166, 35, 0.35) !important;
  color: #f5a623 !important;
}

.project-table {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025);
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 100px 280px 24px;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.25);
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 100px 280px 24px;
  align-items: center;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 0.15s;
}

.table-row:hover {
  background: rgba(245, 166, 35, 0.06);
}

.project-name {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: #e8e6e3;
}

.adapter-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
  background: rgba(245, 166, 35, 0.18);
  color: #f5a623;
}

.col-uuid {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.row-arrow {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
  text-align: right;
  transition: color 0.15s;
}

.table-row:hover .row-arrow { color: #f5a623; }

/* Right column panels */
.feed-panel,
.provider-panel {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.35);
}

.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.feed-body {
  padding: 4px 0;
}

.feed-empty {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
  padding: 24px 0;
}

.provider-body {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.provider-row {
  display: grid;
  grid-template-columns: 80px 1fr 48px;
  align-items: center;
  gap: 8px;
}

.provider-name {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.5);
  text-transform: capitalize;
}

.provider-bar-wrap {
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
}

.provider-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s ease;
}

.mono {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.35);
  text-align: right;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #111113;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 480px;
  max-width: calc(100vw - 32px);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.45);
}

.modal-close {
  background: none;
  border: none;
  color: rgba(232, 230, 227, 0.3);
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: color 0.15s;
}

.modal-close:hover { color: #e8e6e3; }

.modal-form {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.35);
}

.field-input {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 9px 12px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
  outline: none;
  transition: border-color 0.2s;
}

.field-input:focus { border-color: rgba(245, 166, 35, 0.4); }
.field-input::placeholder { color: rgba(232, 230, 227, 0.2); }

.field-textarea {
  resize: vertical;
  min-height: 72px;
}

.field-select option { background: #111113; }

.form-error {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f87171;
  padding: 8px 12px;
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.2);
  border-radius: 4px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
}

.cancel-btn {
  padding: 8px 16px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(232, 230, 227, 0.45);
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover:not(:disabled) { border-color: rgba(255, 255, 255, 0.15); color: #e8e6e3; }
.cancel-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.submit-btn {
  padding: 8px 18px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.submit-btn:hover:not(:disabled) { opacity: 0.85; }
.submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
